<template>
	<div class="time-widget">
		<roller-select :list="hourList" v-model="hour" style="text-align: right" unset-value="--"></roller-select>
		<span style="margin-top: 64px; height: 32px; line-height: 32px">:</span>
		<roller-select :list="minList" v-model="min" unset-value="--"></roller-select>
	</div>
</template>

<script setup lang="ts">
import RollerSelect from "../../helpers/RollerSelect.vue";
import {computed} from "vue";
import {parseDate, updateIsoHour, updateIsoMin} from "@t/util/DateUtil";
import type {ListItemBase} from "@t/list/List";

const props = defineProps({
  modelValue: String
});
const emit = defineEmits(["update:modelValue"]);

const STEP = 5;

function hr(val:number) {
	let s = val.toString();
	return s.length < 2 ? "0" + s : s;
}

function alignMinutes(mins: number | null): number|null {
	if (mins === null) return null;
	mins = Math.round(mins / STEP) * STEP;
	if (mins < 0) mins = 0;
	else if (mins > 23) mins = mins % 60;
	return mins;
}

const hour = computed<string | undefined>({
  get() {
    return getTimeValue((d:Date) => d.getHours());
  },
  set(v) {
    let vnum: number|null;
    if (v != null) {
      vnum = parseInt(v.toString());
      if (vnum < 0) vnum = 0;
      else if (vnum > 23) vnum = vnum % 24;
    } else vnum = null;

    let newValue = updateIsoHour(props.modelValue, vnum);
    if (props.modelValue !== newValue) {
      emit("update:modelValue", newValue);
    }
  }
});

const min = computed<string | undefined>({
  get() {
    return getTimeValue((d:Date) => alignMinutes(d.getMinutes()));
  },
  set(v) {
    let vnum: number|null = null;
    if (v != null) {
      vnum = parseInt(v.toString());
    }
    let alignMin = alignMinutes(vnum);
    let newValue = updateIsoMin(props.modelValue, alignMin);
    if (props.modelValue !== newValue) {
      emit("update:modelValue", newValue);
    }
  },
});

const hourList = computed<ListItemBase[]>(() => {
  let l = [];
  for (let h = 0; h < 24; h++) l.push({ name: hr(h), value: h.toString() });
  return l;
});

const minList = computed<ListItemBase[]>(() => {
  let l = [];
  for (let h = 0; h < 60; h += 5) l.push({ name: hr(h), value: h.toString() });
  return l;
});

/**
 * @param {function} dateGetter
 */
function getTimeValue(dateGetter:(x:Date)=>number|null):string|undefined {
  const d = parseDate(props.modelValue);
  if (d.date == null || d.wholeDay) return undefined; // no time
  let obtained = dateGetter(d.date);
  return obtained == null ? undefined : obtained.toString();
}
</script>

<style>
.time-widget {
	display: flex;
	flex-direction: row;
	column-gap: 8px;
	align-items: flex-start;
}
</style>
